import React, {useEffect, useRef, useState} from "react";

type User = {email:string; name:string};
type Background = {id:string; url:string; filename:string};
const TTL = 10 * 60 * 1000;
async function me(): Promise<User | null> {
  const response = await fetch("/api/auth/me",{credentials:"same-origin",cache:"no-store"});
  if (response.status === 401) return null;
  if (!response.ok) throw new Error("Could not check your Coverbaker account");
  return (await response.json()).user;
}
export default function PointCloudImport() {
  const [enabled,setEnabled] = useState(false), [local,setLocal] = useState(false), [eligible,setEligible] = useState(false);
  const [user,setUser] = useState<User | null>(null), [blob,setBlob] = useState<Blob | null>(null);
  const [preview,setPreview] = useState(""), [message,setMessage] = useState("Checking Coverbaker…");
  const [busy,setBusy] = useState(false), [saved,setSaved] = useState<Background | null>(null);
  const request = useRef(crypto.randomUUID()), fileName = useRef("point-cloud.png");
  const bridge = useRef<{nonce:string; source:string; opener:Window} | null>(null);
  const consumed = useRef(false), expired = useRef(false), auth = useRef<User | null>(null);
  function notify(type:string, extra = {}) {
    const target = bridge.current;
    if (target) target.opener.postMessage({type,version:1,nonce:target.nonce,...extra},target.source);
  }
  async function refreshAccount() {
    try { const current = await me(); setUser(current); auth.current=null; setEligible(false);
      if (!current) setMessage("Sign in to Coverbaker in a separate window, then continue here.");
      else {
        const response = await fetch("/api/point-cloud/config", {credentials:"same-origin", cache:"no-store"});
        if (!response.ok) throw new Error("Could not check Point Cloud import availability.");
        const config = await response.json();
        if (!config.enabled || (!config.localPrototype && !config.availableToCurrentAccount)) {
          setMessage("Point Cloud import is not enabled for this account. Sign in with another Google account.");
          return;
        }
        auth.current=current; setEligible(true);
        setMessage("Confirm this account to add the background to your library.");
        if (!consumed.current && !expired.current) notify("point-cloud:ready");
      }
    } catch(error: any) { setMessage(error.message); }
  }
  useEffect(() => {
    let stopped=false, maxBytes=50*1024*1024;
    const receive = (event:MessageEvent) => {
      const target = bridge.current, data = event.data;
      if (!target || consumed.current || expired.current || !auth.current || event.origin !== target.source || event.source !== target.opener ||
          !data || data.type !== "point-cloud:image" || data.version !== 1 || data.nonce !== target.nonce ||
          !(data.blob instanceof Blob) || data.blob.type !== "image/png" || data.blob.size < 1 || data.blob.size > maxBytes ||
          typeof data.requestId !== "string" || !/^[a-f0-9-]{36}$/.test(data.requestId)) return;
      consumed.current=true; request.current=data.requestId; fileName.current="point-cloud.png";
      setBlob(data.blob); notify("point-cloud:received");
    };
    window.addEventListener("message",receive);
    fetch("/api/point-cloud/config",{cache:"no-store"}).then(async (response) => {
      if (!response.ok) throw new Error("Could not check Point Cloud import availability.");
      const config = await response.json(); if (!config.enabled) throw new Error("Point Cloud import is disabled.");
      if (stopped) return; setEnabled(true); setLocal(config.localPrototype === true); maxBytes=config.maxBytes;
      const params = new URLSearchParams(location.hash.slice(1));
      const source = params.get("source"), nonce=params.get("nonce");
      if (source && nonce && /^[a-f0-9-]{36}$/.test(nonce) && config.allowedSourceOrigins.includes(source) && window.opener) bridge.current={source,nonce,opener:window.opener};
      await refreshAccount();
    }).catch((error) => { if (!stopped) setMessage(error.message); });
    const timer = setTimeout(() => { expired.current=true; bridge.current=null; setBlob(null); setMessage("Transfer expired. Send the picture again or choose a downloaded PNG."); },TTL);
    return () => { stopped=true; clearTimeout(timer); window.removeEventListener("message",receive); bridge.current=null; };
  },[]);
  useEffect(() => {
    if (!blob) { setPreview(""); return; }
    const url=URL.createObjectURL(blob); setPreview(url); return () => URL.revokeObjectURL(url);
  },[blob]);
  async function save() {
    if (!blob || !user || busy || !eligible) return;
    setBusy(true);
    try {
      const current = await me();
      if (!current || current.email !== user.email) { auth.current=null; setUser(current); setEligible(false); throw new Error("Your account changed. Check the account and confirm again."); }
      const form = new FormData(); form.append("file",blob,fileName.current); form.append("requestId",request.current); form.append("confirmedEmail",current.email);
      const response=await fetch("/api/point-cloud/backgrounds",{method:"POST",body:form,credentials:"same-origin",signal:AbortSignal.timeout(60_000)});
      const result=await response.json(); if (!response.ok) throw new Error(result.error || "Import failed");
      if (!result.id || !result.url) throw new Error("Invalid import response");
      setSaved(result); setMessage("Saved in your profile. Choose where to use it.");
      notify("point-cloud:imported",{requestId:request.current}); setBlob(null);
    } catch(error: any) { setMessage(error.name === "TimeoutError" ? "Upload timed out. Retry keeps the same request and will not create a duplicate." : error.message); }
    finally { setBusy(false); }
  }
  return <div className="point-cloud-import" role="main" style={{maxWidth:760,margin:"40px auto",padding:24,color:"#eee",background:"#242429",font:"15px system-ui",borderRadius:12}}>
    <style>{"body:has(.point-cloud-import){min-width:0}"}</style>
    <h1 style={{fontSize:26,marginBottom:16}}>Point Cloud → Coverbaker</h1>
    <p style={{marginBottom:16}}>{message}</p>
    {enabled && <>
      <p style={{marginBottom:16}}>Account: <strong data-testid="import-account">{user?.email || "Not signed in"}</strong></p>
      {!saved && (!user || !eligible) && <div style={{display:"flex",gap:12,marginBottom:16}}>
        <button onClick={() => window.open("/auth/login?next=%2Fimport%2Fpoint-cloud","_blank","popup,width=600,height=700")}>{user ? "Switch account" : local ? "Sign in (local test account)" : "Sign in with Google"}</button>
        <button onClick={refreshAccount}>Continue after login</button>
      </div>}
      {user && eligible && !saved && <>
        {preview && <img src={preview} alt="Point Cloud preview" style={{maxWidth:"100%",maxHeight:400,background:"#111",marginBottom:16}} />}
        <p style={{marginBottom:16}}>This import is added only to your profile’s library. Images are stored on a public CDN: anyone with the image URL can open it. Removing an import does not revoke that URL or change saved scenes.</p>
        <label style={{display:"block",marginBottom:16}}>Or choose a downloaded PNG: <input aria-label="Choose PNG" type="file" accept="image/png" disabled={busy} onChange={(event) => {
          const file=event.target.files?.[0]; if (!file) return;
          if (file.type !== "image/png" || file.size > 50*1024*1024) { setMessage("Choose a PNG up to 50 MiB."); return; }
          consumed.current=true; request.current=crypto.randomUUID(); fileName.current=file.name; setBlob(file); setSaved(null);
        }} /></label>
        <button data-testid="confirm-import" disabled={!blob || busy || !eligible} onClick={save}>{busy ? "Saving…" : `Save to ${user.email}`}</button>
      </>}
      {saved && <section><h2 style={{margin:"16px 0"}}>Use as background</h2>
        <p style={{marginBottom:16}}>Your existing Coverbaker controls remain available in the editor.</p>
        <a style={{color:"#9cf",marginRight:24}} href={`/social/JB/general?background=custom&privateBackground=${saved.id}`}>Open Social editor</a>
        <a style={{color:"#9cf"}} href={`/ads/JB/ai?background=custom&privateBackground=${saved.id}`}>Open Ads editor</a>
      </section>}
    </>}
  </div>;
}
